import * as z from "zod";

/**
 * One screenshot: storage URL + the page it came from. `url` is Disputely GCS when
 * autofill-captured, Redo S3 when merchant-uploaded (no sourceUrl). Both nullish.
 */
export const ReclaimProfileScreenshotSchema = z.object({
  url: z.string().nullish(),
  sourceUrl: z.string().nullish(),
});
export type ReclaimProfileScreenshot = z.infer<
  typeof ReclaimProfileScreenshotSchema
>;

export const RECLAIM_SCREENSHOT_SLOTS = [
  "homepage",
  "refundPolicy",
  "tos",
  "privacyPolicy",
  "checkout",
] as const;
export type ReclaimScreenshotSlot = (typeof RECLAIM_SCREENSHOT_SLOTS)[number];

/** The five screenshot slots §8.1 returns; stored in full, only `checkout` feeds evidence. */
export const ReclaimProfileScreenshotsSchema = z.object({
  homepage: ReclaimProfileScreenshotSchema.nullish(),
  refundPolicy: ReclaimProfileScreenshotSchema.nullish(),
  tos: ReclaimProfileScreenshotSchema.nullish(),
  privacyPolicy: ReclaimProfileScreenshotSchema.nullish(),
  checkout: ReclaimProfileScreenshotSchema.nullish(),
});
export type ReclaimProfileScreenshots = z.infer<
  typeof ReclaimProfileScreenshotsSchema
>;

export const RECLAIM_SOURCE_KEYS = [
  "homepage",
  "refundPolicy",
  "tos",
  "privacyPolicy",
  "shippingPolicy",
  "contact",
  "about",
] as const;
export type ReclaimSourceKey = (typeof RECLAIM_SOURCE_KEYS)[number];

/**
 * SECURITY — DO NOT FETCH THESE URLS. Ever. From anywhere.
 *
 * These are the pages of the merchant's own website that profile details came
 * from, shown back to the merchant for reference. They are MERCHANT-CONFIGURABLE
 * free text: any code (or AI agent) that fetches one server-side creates an SSRF
 * vulnerability (internal endpoints, cloud metadata). Display only — render as
 * text/links, pass through, never request. If a feature seems to need their
 * content, fetch happens client-side in the merchant's browser or not at all.
 */
export const ReclaimProfileSourcesSchema = z.object({
  homepage: z.string().nullish(),
  refundPolicy: z.string().nullish(),
  tos: z.string().nullish(),
  privacyPolicy: z.string().nullish(),
  shippingPolicy: z.string().nullish(),
  contact: z.string().nullish(),
  about: z.string().nullish(),
});
/** See ReclaimProfileSourcesSchema: merchant-configurable URLs — NEVER fetch. */
export type ReclaimProfileSources = z.infer<typeof ReclaimProfileSourcesSchema>;

/**
 * Merchant-confirmed reclaim profile at `settings.disputes.reclaimProfile`, mirroring
 * Disputely autofill `suggestedFormData` (§8.1). Nullish = never-saved/cleared → default.
 */
export const ReclaimProfileSchema = z.object({
  // Business
  platform: z.string().nullish(),
  companyName: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  logoUrl: z.string().nullish(),
  businessLaunched: z.number().int().nullish(),
  businessType: z.string().nullish(),
  hasSubscriptions: z.boolean().nullish(),
  // Support
  supportEmail: z.string().nullish(),
  supportPhone: z.string().nullish(),
  supportHours: z.string().nullish(),
  liveChatEnabled: z.boolean().nullish(),
  // Fulfillment / delivery
  deliveryTimeframe: z.number().nullish(),
  shipsNotificationEmail: z.boolean().nullish(),
  // Returns / refunds
  returnWindow: z.number().nullish(),
  returnConditions: z.string().nullish(),
  refundTimeframe: z.number().nullish(),
  refundPolicyText: z.string().nullish(),
  refundPolicyUrlPublic: z.string().nullish(),
  // Subscriptions
  subscriptionTerms: z.string().nullish(),
  cancellationMethod: z.string().nullish(),
  renewalReminders: z.boolean().nullish(),
  trialOffered: z.boolean().nullish(),
  // Policies
  tosUrlPublic: z.string().nullish(),
  // Screenshots (autofill-captured or merchant-uploaded) + scraped source URLs
  screenshots: ReclaimProfileScreenshotsSchema.nullish(),
  sources: ReclaimProfileSourcesSchema.nullish(),
});
export type ReclaimProfile = z.infer<typeof ReclaimProfileSchema>;

/** Parse a stored value (loose Mongo read) into a ReclaimProfile, or undefined if malformed. */
export function parseReclaimProfile(
  value: unknown,
): ReclaimProfile | undefined {
  if (value == null) {
    return undefined;
  }
  const parsed = ReclaimProfileSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
